<template>
  <transition name="el-notification-fade">
    <div
      v-show="display"
      class="el-notification"
      :title="hoverHint"
      :style="positionStyle"
      :class="[
        customClass,
        horizontalClass,
        type ? `el-notification--${type}` : '',
        {'is-closable': showClose}
      ]"
      role="alert"
      @mouseenter="clearTimer()"
      @mouseleave="startTimer()"
      @click="click"
    >
      <div class="el-notification__container">
        <div
          v-if="isIconShown"
          class="el-notification__icon"
          :class="{
            iconClass,
            'el-notification__icon--align-message':hasMessage,
            'el-notification__icon--align-title':hasTitle
          }"
        >
          <el-spinner
            v-if="type === 'progress'"
            size="sm"
          />
          <el-icon
            v-else
            :name="iconName"
          />
        </div>
        <div class="el-notification__content">
          <slot>
            <div
              v-if="title"
              class="el-notification__title"
            >
              {{ title }}
            </div>
            <component
              :is="message"
              v-if="isComponent"
            />
            <template v-else>
              <div
                v-if="dangerouslyUseHTMLString"
                v-html="message"
              />
              <div
                v-else
                class="el-notification__message"
              >
                {{ message }}
              </div>
              <div v-if="hasRequestId">
                <span style="font-size: 14px;">
                  <p/><div style='display: flex; justify-content: space-between;'>
                    <textarea
                      :id=popUpId
                      v-model=requestId
                      style='border: none; background-color: transparent; resize: none; outline: none;
          min-width: 190px; min-height: 50px;
          overflow: hidden; font-size: 13px;'/>
                    <el-button
                      @click="copyRequestId"
                      type="ghost">
                      {{ copyButtonName }}
                    </el-button>
                  </div>
                </span>

              </div>
            </template>
          </slot>
        </div>
      </div>
      <div
        v-if="showClose"
        class="el-notification__close"
        tabindex="0"
        @click.stop="close"
        @keydown.enter="close"
      >
        <el-icon name="i-times--16" />
      </div>
    </div>
  </transition>
</template>

<script type="text/babel">
import ElIcon from '@uikit/ui-kit/packages/icon';
import ElSpinner from '@uikit/ui-kit/packages/spinner';

const TYPES_MAP = {
  success: 'i-check-circle--16',
  warning: 'i-exclamation-triangle--16',
  error: 'i-times-circle--16',
  info: 'i-info-circle--16'
};

export default {
  name: 'ElNotificationComponentEx',

  componentName: 'ElNotificationComponentEx',

  components: {
    ElIcon,
    ElSpinner
  },

  props: {
    visible: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: ''
    },
    iconClass: {
      type: String,
      default: ''
    },
    customClass: {
      type: String,
      default: ''
    },
    showClose: {
      type: Boolean,
      default: true
    },
    onClose: {
      type: Function,
      default: () => {}
    },
    onClick: {
      type: Function,
      default: () => {}
    },
    position: {
      type: String,
      default: 'bottom-right',
      validator: value => ['top-right', 'top-left', 'bottom-right', 'bottom-left'].indexOf(value) > -1
    },
    duration: {
      type: Number,
      default: 4500
    },
    showHoverHint: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    keepAlive: true,
    display: false,
    closed: false,
    timer: null,
    title: '',
    message: '',
    requestId: '',
    copyButtonName: 'Copy',
    popUpId: '',
    dangerouslyUseHTMLString: false,
    verticalOffset: 0
  }),

  computed: {
    iconName() {
      return this.type ? TYPES_MAP[this.type] : '';
    },

    horizontalClass() {
      return this.position.split('-')[1];
    },

    verticalProperty() {
      return this.position.split('-')[0];
    },

    positionStyle() {
      return {
        [this.verticalProperty]: `${this.verticalOffset}px`
      };
    },

    isComponent() {
      return this.message instanceof Function;
    },

    isIconShown() {
      return this.iconClass || this.iconName || this.type === 'progress';
    },

    hoverHint() {
      return this.showHoverHint ? this.title : null;
    },

    hasTitle() {
      return this.title && this.title.length > 0;
    },

    hasMessage() {
      return this.message && this.message.length > 0;
    },

    hasRequestId() {
      return this.requestId && this.requestId.length > 0;
    }
  },

  watch: {
    visible(newVal) {
      if (newVal) {
        this.show();
      } else {
        this.hide();
      }
    },

    closed(newVal) {
      this.$emit('update:visible', !newVal);
      if (!this.keepAlive) {
        //          this.$el.addEventListener('transitionend', this.destroyElement);
        this.destroyElement();
      }
    }
  },

  mounted() {
    this.popUpId = `error-f${(~~(Math.random() * 1e8)).toString(16)}`;
    this.visible && this.show();
    if (this.duration > 0 && !this.closed) {
      this.timer = setTimeout(() => {
        this.close();
      }, this.duration);
    }
    document.addEventListener('keydown', this.keydown);
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.keydown);
  },

  methods: {
    destroyElement() {
      //        this.$el.removeEventListener('transitionend', this.destroyElement);
      this.$destroy(true);
      const el = this.$el;
      if (el.remove) {
        // everyone else except IE
        // https://developer.mozilla.org/en-US/docs/Web/API/ChildNode/remove
        el.remove();
      } else if (el.parentNode && el.parentNode.removeChild) {
        // IE
        el.parentNode.removeChild(el);
      }
    },

    copyRequestId() {
      const copyText = document.getElementById(this.popUpId);
      copyText.select();
      copyText.setSelectionRange(0, 99999);
      document.execCommand('copy');
    },

    show() {
      this.display = true;
      this.closed = false;

      this.id = this.$notify.getId();
      this.$notify.position(this);
      this.$notify.register(this);

      if (this.duration > 0) {
        this.timer = setTimeout(() => { this.hide(); }, this.duration);
      }
    },

    hide() {
      this.display = false;
      this.$notify.close(this.id, () => !this.closed && this.onClose());
    },

    click() {
      this.onClick();
    },

    close() {
      this.closed = true;
      this.onClose();
    },

    clearTimer() {
      clearTimeout(this.timer);
    },

    startTimer() {
      if (this.duration > 0) {
        this.timer = setTimeout(() => {
          if (!this.closed) {
            this.close();
          }
        }, this.duration);
      }
    },

    keydown(e) {
      if (e.keyCode === 46 || e.keyCode === 8) {
        this.clearTimer();
      } else if (e.keyCode === 27) {
        if (!this.closed) {
          this.close();
        }
      } else {
        this.startTimer();
      }
    }
  }
};
</script>
